/**
 * Web-Speech-API-backed "Hey Koko" wake word listener.
 *
 * The native app uses `SFSpeechRecognizer` with
 * `requiresOnDeviceRecognition = true` so nothing ever leaves the Mac.
 * Browsers don't expose an equivalent on-device recognizer; Chrome and
 * Edge ship `webkitSpeechRecognition` which streams audio to Google's
 * servers. Firefox and Safari do not. We still match the rest of the
 * native behavior:
 *
 *   - Always-on while enabled.
 *   - Detects "hey koko" plus phonetic variants (hey coco, hey cocoa, etc.)
 *     matching the phrase list in KokoWakeWordListener.swift:63-106.
 *   - Auto-restarts the recognition session when it ends (browsers end
 *     the stream after ~60s or after silence).
 *   - Dormant while TTS is playing to avoid echo-triggering itself.
 *
 * If the browser doesn't support SpeechRecognition at all, `isSupported`
 * returns false and the control panel shows an unsupported hint.
 *
 * Diagnostics — added so users can see *why* nothing is happening:
 *   - `wakeWordStatus` reactive signal: idle | starting | listening |
 *     restarting | blocked.
 *   - `recentTranscripts`: rolling ring of the last few things the
 *     recognizer heard. Surfaced in the control panel so a user
 *     reporting "wake word doesn't work" can see at a glance whether
 *     the recognizer heard "hey kogo" (phrase mismatch) or never heard
 *     anything at all (mic / network problem).
 *   - Pre-flight mic permission gate: explicitly request
 *     `getUserMedia` before starting the recognizer. Without this, the
 *     recognizer fails opaquely the first time and the user sees
 *     "wake word: ON" with no signal that mic access is what's missing.
 */

const WAKE_PHRASES = [
  "hey koko",
  "hey coco",
  "hey cocoa",
  "hey coca",
  "hey cola",
  "hey kogo",
  "hey cogo",
  "hay koko",
  "hay coco",
  "hey kiki",
  "hey kookoo",
  "hey ko ko",
  "hey co co",
  "koko",
  "kokoa",
  "coco",
  "kogo",
  "kookoo",
];

type BrowserSpeechRecognition = EventTarget & {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: ((event: Event) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onstart: ((event: Event) => void) | null;
};

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionConstructor {
  new (): BrowserSpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export type WakeWordStatusKind =
  | "idle"
  | "starting"
  | "listening"
  | "restarting"
  | "blocked";

export interface WakeWordStatus {
  kind: WakeWordStatusKind;
  /** Underlying error code when `kind === "blocked"`, e.g. "not-allowed". */
  reason?: string;
}

const TRANSCRIPT_RING_SIZE = 5;

export interface WakeWordListenerOptions {
  onDetected: () => void;
  /** While TTS is playing the listener is dormant so it can't hear itself. */
  isOutputtingAudio: () => boolean;
  /** Surface unexpected errors so the panel UI can warn the user. */
  onError?: (error: WakeWordError) => void;
  /**
   * Reactive status updates. Lets the control panel show a green /
   * yellow / red dot describing the listener's actual state instead
   * of just trusting the `wakeWordEnabled` toggle.
   */
  onStatusChange?: (status: WakeWordStatus) => void;
  /**
   * Fires whenever the recognizer reports a non-empty transcript.
   * Powers the "recent transcripts" debug surface in the panel — the
   * single most useful signal when wake-word phrase matching fails
   * (e.g. user says "hey koko" but Chrome heard "hey kogo").
   */
  onTranscriptHeard?: (transcript: string) => void;
}

export interface WakeWordError {
  /** SpeechRecognition error code: "not-allowed", "no-speech", etc. */
  code: string;
  message: string;
  /** True when the listener gave up retrying (mic denied, browser refused, etc). */
  fatal: boolean;
}

const FATAL_ERROR_CODES = new Set([
  "not-allowed",
  "service-not-allowed",
  "audio-capture",
  "language-not-supported",
]);

const RESTART_BACKOFF_MS = 400;

export class WakeWordListener {
  private readonly options: WakeWordListenerOptions;
  private recognizer: BrowserSpeechRecognition | null = null;
  private isEnabled = false;
  private didFireForCurrentSession = false;
  private restartTimerId: number | null = null;
  private consecutiveFailures = 0;
  private hasMicPermission = false;
  private status: WakeWordStatus = { kind: "idle" };
  private readonly recentTranscripts: string[] = [];

  constructor(options: WakeWordListenerOptions) {
    this.options = options;
  }

  static isSupported(): boolean {
    const ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    return typeof ctor === "function";
  }

  getStatus(): WakeWordStatus {
    return this.status;
  }

  /** Last few transcripts heard. Returned newest-first for easy display. */
  getRecentTranscripts(): readonly string[] {
    return [...this.recentTranscripts].reverse();
  }

  async enable(): Promise<void> {
    if (!WakeWordListener.isSupported()) {
      this.setStatus({ kind: "blocked", reason: "unsupported" });
      return;
    }
    if (this.isEnabled) return;
    this.isEnabled = true;
    this.consecutiveFailures = 0;
    this.setStatus({ kind: "starting" });

    // Pre-flight mic permission gate. The recognizer fails with an
    // opaque "not-allowed" error on its first start() if mic permission
    // hasn't been granted yet, and that error sometimes doesn't even
    // surface — the user just sees "wake word: ON" doing nothing. Forcing
    // a getUserMedia() first triggers the browser permission prompt
    // synchronously and gives us a clean failure mode if denied.
    if (!this.hasMicPermission) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately stop the tracks — we don't need the stream itself,
        // we just needed the permission prompt to fire. The recognizer
        // opens its own internal mic graph.
        stream.getTracks().forEach((track) => track.stop());
        this.hasMicPermission = true;
      } catch (error) {
        const code = (error as DOMException)?.name === "NotAllowedError"
          ? "not-allowed"
          : (error as DOMException)?.name === "NotFoundError"
            ? "audio-capture"
            : "permission-failed";
        this.setStatus({ kind: "blocked", reason: code });
        this.options.onError?.({
          code,
          message: (error as Error)?.message ?? code,
          fatal: true,
        });
        this.isEnabled = false;
        return;
      }
    }

    this.startRecognizer();
  }

  disable(): void {
    this.isEnabled = false;
    if (this.restartTimerId !== null) {
      window.clearTimeout(this.restartTimerId);
      this.restartTimerId = null;
    }
    if (this.recognizer) {
      // Detach listeners first so the abort doesn't trigger a restart.
      this.recognizer.onresult = null;
      this.recognizer.onend = null;
      this.recognizer.onerror = null;
      this.recognizer.onstart = null;
      try {
        this.recognizer.abort();
      } catch {
        // Recognizer already stopped; swallow.
      }
      this.recognizer = null;
    }
    this.setStatus({ kind: "idle" });
  }

  private startRecognizer(): void {
    if (!this.isEnabled) return;
    const Constructor =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Constructor) return;

    const recognizer = new Constructor();
    recognizer.continuous = true;
    recognizer.interimResults = true;
    recognizer.lang = "en-US";

    this.didFireForCurrentSession = false;
    this.recognizer = recognizer;

    recognizer.onstart = () => {
      this.consecutiveFailures = 0;
      this.setStatus({ kind: "listening" });
    };
    recognizer.onresult = (event) => this.handleResult(event);
    recognizer.onerror = (event) => this.handleError(event);
    recognizer.onend = () => this.handleEnd();

    try {
      recognizer.start();
    } catch (error) {
      // "InvalidStateError" or "already started" — schedule a restart.
      this.scheduleRestart();
      this.options.onError?.({
        code: "start-threw",
        message: (error as Error)?.message ?? "recognition.start() threw",
        fatal: false,
      });
    }
  }

  private handleResult(event: SpeechRecognitionEvent): void {
    if (this.didFireForCurrentSession) return;
    if (this.options.isOutputtingAudio()) return;

    for (let resultIndex = event.resultIndex; resultIndex < event.results.length; resultIndex++) {
      const result = event.results[resultIndex];
      for (let altIndex = 0; altIndex < result.length; altIndex++) {
        const transcript = normalizeTranscript(result[altIndex].transcript);
        if (transcript.length > 0) {
          // Diagnostic log so a user who says "hey koko" but doesn't
          // see Koko react can check DevTools to see what the recognizer
          // actually transcribed. Most "wake word doesn't work" reports
          // turn out to be the recognizer hearing "hey cocoa" or similar.
          console.debug("[koko] wake heard:", transcript);
          this.recordTranscript(transcript);
        }
        if (containsWakePhrase(transcript)) {
          this.didFireForCurrentSession = true;
          // Tear the recognizer down before invoking the callback —
          // `onDetected` will start AssemblyAI mic capture, and two
          // simultaneous mic clients on the same device misbehave.
          this.disable();
          this.options.onDetected();
          return;
        }
      }
    }
  }

  private handleError(event: SpeechRecognitionErrorEvent): void {
    const code = event.error || "unknown";
    const isFatal = FATAL_ERROR_CODES.has(code);
    this.options.onError?.({
      code,
      message: event.message || code,
      fatal: isFatal,
    });
    if (isFatal) {
      // Permission was denied or the browser refused service. Stop
      // retrying — a hot loop here would burn battery and hammer the
      // permission prompt forever.
      this.setStatus({ kind: "blocked", reason: code });
      this.disable();
    }
  }

  private handleEnd(): void {
    this.recognizer = null;
    if (!this.isEnabled) return;
    // Browsers end the stream after silence or ~60s. Re-arm so
    // wake-word detection stays always-on, with a small backoff if we
    // keep failing immediately (network hiccup, browser oddity).
    this.consecutiveFailures = Math.min(this.consecutiveFailures + 1, 6);
    this.setStatus({ kind: "restarting" });
    this.scheduleRestart();
  }

  private scheduleRestart(): void {
    if (this.restartTimerId !== null) return;
    if (!this.isEnabled) return;
    const delay = Math.min(
      RESTART_BACKOFF_MS * Math.max(1, this.consecutiveFailures),
      4000,
    );
    this.restartTimerId = window.setTimeout(() => {
      this.restartTimerId = null;
      this.startRecognizer();
    }, delay);
  }

  private setStatus(next: WakeWordStatus): void {
    if (
      next.kind === this.status.kind &&
      next.reason === this.status.reason
    ) {
      return;
    }
    this.status = next;
    this.options.onStatusChange?.(next);
  }

  private recordTranscript(transcript: string): void {
    this.recentTranscripts.push(transcript);
    if (this.recentTranscripts.length > TRANSCRIPT_RING_SIZE) {
      this.recentTranscripts.shift();
    }
    this.options.onTranscriptHeard?.(transcript);
  }
}

function normalizeTranscript(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function containsWakePhrase(normalizedText: string): boolean {
  if (normalizedText.length === 0) return false;
  return WAKE_PHRASES.some((phrase) => normalizedText.includes(phrase));
}
